import { Navigate, Route, Router, useLocation, useNavigate } from '@solidjs/router';
import { QueryClient, QueryClientProvider } from '@tanstack/solid-query';
import { createEffect, createResource, Match, Switch, type JSX } from 'solid-js';
import { AppShell } from './components/AppShell';
import { ErrorAlert, Toaster } from './components/states';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { loadConfig } from './lib/config';
import { AdminStacks } from './routes/AdminStacks';
import { BoxCreate } from './routes/BoxCreate';
import { BoxDetail } from './routes/BoxDetail';
import { Boxes } from './routes/Boxes';
import { NotFound } from './routes/NotFound';
import { SignIn } from './routes/SignIn';
import { SshKeys } from './routes/SshKeys';
import { Stacks } from './routes/Stacks';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Layout(props: { children?: JSX.Element }): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  createEffect(() => {
    if (auth.status() !== 'authenticated') return;
    const target = auth.takeRedirect();
    if (target && target !== location.pathname + location.search) {
      navigate(target, { replace: true });
    }
  });

  return (
    <>
      <Switch>
        <Match when={auth.status() === 'starting'}>
          <div class="boot">
            <span class="tk-spinner tk-spinner--lg" />
            <span class="text-sm">Restoring your session…</span>
          </div>
        </Match>
        <Match when={auth.status() === 'anonymous'}>
          <SignIn />
        </Match>
        <Match when={auth.status() === 'authenticated'}>
          <AppShell>{props.children}</AppShell>
        </Match>
      </Switch>
      <Toaster />
    </>
  );
}

export function App(): JSX.Element {
  const [config] = createResource(loadConfig);

  return (
    <Switch>
      <Match when={config.loading}>
        <div class="boot">
          <span class="tk-spinner tk-spinner--lg" />
        </div>
      </Match>
      <Match when={config.error}>
        <div class="page page--narrow" style={{ 'padding-top': 'var(--space-16)' }}>
          <ErrorAlert title="The app is not configured" error={config.error} />
          <p class="text-sm secondary" style={{ 'margin-top': 'var(--space-4)' }}>
            public/config.json must provide apiBaseUrl, cognitoDomain and cognitoClientId.
          </p>
        </div>
      </Match>
      <Match when={config()}>
        {(loaded) => (
          <QueryClientProvider client={queryClient}>
            <AuthProvider config={loaded()}>
              <Router root={Layout}>
                <Route path="/" component={() => <Navigate href="/boxes" />} />
                <Route path="/boxes" component={Boxes} />
                <Route path="/boxes/new" component={BoxCreate} />
                <Route path="/boxes/:boxId" component={BoxDetail} />
                <Route path="/stacks" component={Stacks} />
                <Route path="/ssh-keys" component={SshKeys} />
                <Route path="/admin/stacks" component={AdminStacks} />
                <Route path="*" component={NotFound} />
              </Router>
            </AuthProvider>
          </QueryClientProvider>
        )}
      </Match>
    </Switch>
  );
}
